import React, { useState, useEffect } from "react";
import LandingHeader from "../components/layout/LandingHeader";
import LandingFooter from "../components/layout/LandingFooter";
import {
  CheckSquare,
  User,
  Briefcase,
  RotateCcw,
  Bookmark,
  AlertTriangle,
  RefreshCw,
  MapPin,
  UserCheck,
  Calendar,
} from "lucide-react";

const sections = [
  { id: "aceitacao", title: "1. Aceitação dos Termos", icon: CheckSquare },
  { id: "cadastro", title: "2. Cadastro e Segurança", icon: User },
  { id: "servicos", title: "3. Serviços e Responsabilidades", icon: Briefcase },
  { id: "reembolsos", title: "4. Cancelamento e Reembolso", icon: RotateCcw },
  { id: "propriedade-intelectual", title: "5. Propriedade Intelectual", icon: Bookmark },
  { id: "limitacao", title: "6. Limitação de Responsabilidade", icon: AlertTriangle },
  { id: "modificacoes", title: "7. Alterações nos Termos", icon: RefreshCw },
  { id: "foro", title: "8. Legislação e Foro", icon: MapPin },
];

function TermosServico() {
  const [activeSection, setActiveSection] = useState("aceitacao");

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y =
        element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveSection(id);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const top = element.offsetTop;
          const height = element.offsetHeight;

          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-urb-dark text-slate-100 selection:bg-urb-primary selection:text-black">
      <LandingHeader />

      <main className="flex-1 py-10 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col gap-8">
          {/* Header Banner */}
          <div className="flex flex-col gap-2 mb-2 text-center md:text-left">
            <span className="font-semibold text-xs sm:text-sm tracking-wider uppercase text-urb-primary">
              URB RACE &bull; CONTRATO DE USO E DIRETRIZES
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black m-0 leading-tight">
              TERMOS DE{" "}
              <span className="text-urb-primary drop-shadow-[0_0_12px_rgba(138,230,0,0.3)]">
                SERVIÇO
              </span>
            </h1>
            <p className="text-slate-400 text-sm sm:text-base m-0 max-w-2xl leading-relaxed">
              Estes termos regulam o acesso e o uso da plataforma URB RACE. Ao
              navegar ou se cadastrar, você aceita as condições descritas abaixo.
            </p>
            <span className="text-xs text-slate-500 font-medium mt-1">
              Última atualização: 22 de junho de 2026
            </span>
          </div>

          {/* Quick Highlight Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 rounded-2xl border border-urb-primary/20 bg-urb-primary/5 flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <UserCheck className="w-5 h-5 text-urb-primary" />
                <h4 className="m-0 text-white font-bold text-sm">
                  Uso Responsável
                </h4>
              </div>
              <p className="m-0 text-xs text-slate-400 leading-relaxed">
                Seu cadastro é pessoal e intransferível. Você é responsável pelas
                informações cadastradas e pela segurança da sua senha de acesso.
              </p>
            </div>
            <div className="p-5 rounded-2xl border border-urb-primary/20 bg-urb-primary/5 flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <RotateCcw className="w-5 h-5 text-urb-primary" />
                <h4 className="m-0 text-white font-bold text-sm">
                  Reembolso CDC
                </h4>
              </div>
              <p className="m-0 text-xs text-slate-400 leading-relaxed">
                Direito de arrependimento garantido em compras online em até 7
                dias a partir da data de compra, desde que antes do evento.
              </p>
            </div>
            <div className="p-5 rounded-2xl border border-urb-primary/20 bg-urb-primary/5 flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-urb-primary" />
                <h4 className="m-0 text-white font-bold text-sm">
                  Responsabilidade do Evento
                </h4>
              </div>
              <p className="m-0 text-xs text-slate-400 leading-relaxed">
                A URB RACE opera na venda de ingressos e gestão, mas a
                infraestrutura física e a segurança do evento competem ao
                organizador.
              </p>
            </div>
          </div>

          {/* Main Layout Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left Side: Sticky Navigation (Desktop) */}
            <div className="lg:col-span-3 hidden lg:flex flex-col gap-1.5 sticky top-24 pt-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                TÓPICOS DOS TERMOS
              </span>
              <div className="flex flex-col border-l border-slate-800">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={`text-left py-2 px-3 bg-transparent cursor-pointer font-semibold text-xs transition-all flex items-center gap-2 -ml-[1px] border-l-2 ${
                        isActive
                          ? "text-urb-primary border-urb-primary bg-urb-primary/5"
                          : "text-slate-400 border-transparent hover:text-white"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      {section.title}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mobile Horizontal Section Selector */}
            <div className="lg:hidden flex overflow-x-auto gap-2 py-2 mb-2 w-full border-b border-urb-border">
              {sections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    className={`py-1.5 px-3 rounded-lg font-semibold text-xs whitespace-nowrap cursor-pointer flex items-center gap-1.5 border transition-all ${
                      isActive
                        ? "bg-urb-primary border-urb-primary text-black font-bold"
                        : "bg-slate-900 border-slate-800 text-slate-300"
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {section.title.split(". ")[1]}
                  </button>
                );
              })}
            </div>

            {/* Right Side: Detailed Terms Content */}
            <div className="lg:col-span-9 flex flex-col gap-6 text-slate-300 text-xs sm:text-sm leading-relaxed">
              {/* Section 1: Aceitação */}
              <section
                id="aceitacao"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <CheckSquare className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    1. Aceitação dos Termos
                  </h3>
                </div>
                <p>
                  Este Contrato de Termos de Serviço ("Termos") constitui um
                  acordo legal aplicável entre o usuário ("Você") e a plataforma
                  digital <strong>URB RACE</strong>. Ao acessar a plataforma,
                  cadastrar-se ou comprar inscrições para eventos esportivos de
                  corrida de rua, você declara estar ciente e aceitar
                  integralmente estes Termos e as políticas integradas.
                </p>
                <p>
                  Caso não concorde com qualquer disposição aqui estabelecida,
                  solicitamos que não prossiga com o cadastro e interrompa
                  imediatamente o uso de nossos serviços online.
                </p>
              </section>

              {/* Section 2: Cadastro */}
              <section
                id="cadastro"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <User className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    2. Cadastro e Segurança
                  </h3>
                </div>
                <p>
                  Para realizar inscrições em eventos cadastrados em nossa
                  plataforma, o atleta deve preencher o cadastro eletrônico
                  fornecendo dados corretos e verdadeiros. O cadastro é pessoal,
                  único e intransferível.
                </p>
                <p>
                  Você é o único responsável por resguardar a confidencialidade
                  da sua senha de acesso. Quaisquer atividades realizadas na
                  plataforma utilizando sua conta serão de sua inteira
                  responsabilidade jurídica.
                </p>
              </section>

              {/* Section 3: Serviços e Responsabilidades */}
              <section
                id="servicos"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <Briefcase className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    3. Serviços e Responsabilidades
                  </h3>
                </div>
                <p>
                  A URB RACE é um software e marketplace focado na gestão de
                  inscrições online e facilitação do controle financeiro e
                  promocional de corridas de rua. Nosso papel é intermediar a
                  relação cadastral e de pagamentos entre os atletas e os
                  organizadores dos eventos físicos.
                </p>
                <p>
                  <strong>Importante:</strong> A realização física do evento, a
                  segurança da arena de largada, o fornecimento de água nos
                  percursos, o trânsito, a entrega física dos kits e a contratação
                  de equipe médica ou de cronometragem são de responsabilidade
                  direta e exclusiva do <strong>Organizador do Evento</strong>{" "}
                  parceiro.
                </p>
              </section>

              {/* Section 4: Cancelamento e Reembolso */}
              <section
                id="reembolsos"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <RotateCcw className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    4. Cancelamento e Reembolso
                  </h3>
                </div>
                <p>
                  De acordo com o Código de Defesa do Consumidor (CDC), o atleta
                  poderá solicitar o cancelamento e reembolso integral do valor
                  pago pela inscrição em até <strong>7 (sete) dias corridos</strong> a
                  contar da data de confirmação da compra online.
                </p>
                <p>
                  <strong>Exceção:</strong> O prazo limite para solicitação de
                  reembolso é de até 48 horas antes da realização física da prova,
                  de modo a viabilizar os preparativos logísticos do kit e evitar
                  fraudes.
                </p>
              </section>

              {/* Section 5: Propriedade Intelectual */}
              <section
                id="propriedade-intelectual"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <Bookmark className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    5. Propriedade Intelectual
                  </h3>
                </div>
                <p>
                  Todos os direitos de propriedade intelectual sobre a plataforma
                  URB RACE, incluindo códigos-fonte, bancos de dados, design de
                  interfaces, elementos visuais, layouts de página, logos e marcas
                  registradas pertencem exclusivamente à URB RACE.
                </p>
              </section>

              {/* Section 6: Limitação de Responsabilidade */}
              <section
                id="limitacao"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <AlertTriangle className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    6. Limitação de Responsabilidade
                  </h3>
                </div>
                <p>
                  Ao se inscrever em provas de corrida de rua através do nosso
                  sistema, você atesta possuir plenas condições clínicas e físicas
                  para a prática esportiva intensa, isentando a plataforma URB
                  RACE de quaisquer responsabilidades por acidentes, lesões ou
                  danos ocorridos durante os eventos.
                </p>
              </section>

              {/* Section 7: Alterações nos Termos */}
              <section
                id="modificacoes"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <RefreshCw className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    7. Alterações nos Termos
                  </h3>
                </div>
                <p>
                  Reservamo-nos o direito de alterar ou substituir estes Termos
                  de Serviço a qualquer momento, notificando alterações
                  relevantes através do nosso portal.
                </p>
              </section>

              {/* Section 8: Legislação e Foro */}
              <section
                id="foro"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <MapPin className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    8. Legislação e Foro
                  </h3>
                </div>
                <p>
                  Estes Termos são integralmente regidos e interpretados em
                  conformidade com as Leis da República Federativa do Brasil. Fica
                  eleito o foro da comarca da sede administrativa da URB RACE
                  como o competente para dirimir quaisquer controvérsias.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}

export default TermosServico;
